import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { adminClient, type AdminOrganizationRow } from '@/api/adminClient';
import { AddOrganizationDialog } from '@/components/org/AddOrganizationDialog';
import { BillingCell } from '@/components/org/BillingCell';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { SearchInput } from '@/components/table/SearchInput';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { usePagedOffset } from '@/hooks/usePagedOffset';
import { ORG_PAGE_SIZE, formatCount, formatUsd, orgLabel, orgSecondaryLabel } from '@/lib/format';
import { matchesOrgAccessFilter, type OrgAccessFilter } from '@/lib/orgAccess';
import { orgBasePath } from '@/lib/orgNav';

const COLUMNS = [
  { label: 'Organization' },
  { label: 'Studio' },
  { label: 'Projects', className: 'text-right' },
  { label: 'Spend (30d)', className: 'text-right' },
  { label: 'Plan' },
  { label: 'Fabric' },
];

const EMPTY_LABELS: Record<OrgAccessFilter, string> = {
  access: 'No organizations have Studio access yet',
  none: 'Every organization has Studio access',
  all: 'No organizations found',
};

/** The last 30 days' spend as the organization's Money page adds it up: AI building plus integrations. */
export function OrgSpendCell({ usage }: { usage: AdminOrganizationRow['usage30d'] }) {
  const integrations = usage.integrationCostUsd ?? 0;
  const total = usage.totalCostUsd ?? usage.costUsd + integrations;
  return (
    <DataTableCell className="whitespace-nowrap text-right tabular-nums">
      <span title={`${formatCount(usage.runs)} builds in the last 30 days`}>{formatUsd(total)}</span>
      <p className="text-xs text-muted-foreground">
        AI building {formatUsd(usage.costUsd)} · Integrations {formatUsd(integrations)}
      </p>
    </DataTableCell>
  );
}

export function OrganizationsPage() {
  const [items, setItems] = useState<AdminOrganizationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [query, setQuery] = useState('');
  const [accessFilter, setAccessFilter] = useState<OrgAccessFilter>('access');
  const [offset, setOffset] = usePagedOffset(`${query.trim()}|${accessFilter}`);
  const [reloadKey, setReloadKey] = useState(0);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    adminClient
      .listOrganizations()
      .then((result) => {
        if (!cancelled) setItems(result.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load organizations');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);
  const counted = !loading && !error;

  const withAccess = useMemo(() => items.filter((item) => item.studioAccess).length, [items]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      if (!matchesOrgAccessFilter(item.studioAccess, accessFilter)) return false;
      if (!needle) return true;
      return [item.displayName, item.name, item.organizationId].join(' ').toLowerCase().includes(needle);
    });
  }, [items, query, accessFilter]);

  const pageItems = filtered.slice(offset, offset + ORG_PAGE_SIZE);

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Organizations"
        description="WorkOS organizations set up for Studio. Studio access means at least one seat or an invitation."
        actions={
          <Button type="button" onClick={() => setAdding(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Add organization
          </Button>
        }
      />
      <AddOrganizationDialog open={adding} onClose={() => setAdding(false)} onAdded={() => setReloadKey((key) => key + 1)} />
      <DataTable
        toolbar={
          <>
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder="Search name, WorkOS name or org id"
              label="Search organizations"
            />
            <SegmentedFilter<OrgAccessFilter>
              label="Studio access"
              value={accessFilter}
              onChange={setAccessFilter}
              options={[
                { id: 'access', label: 'With access', count: counted ? withAccess : undefined },
                { id: 'none', label: 'No access', count: counted ? items.length - withAccess : undefined },
                { id: 'all', label: 'All', count: counted ? items.length : undefined },
              ]}
            />
          </>
        }
        footer={
          <TablePager total={filtered.length} limit={ORG_PAGE_SIZE} offset={offset} onOffsetChange={setOffset} />
        }
      >
        <DataTableHead columns={COLUMNS} />
        {loading ? (
          <DataTableMessage colSpan={COLUMNS.length} loading>
            Loading organizations
          </DataTableMessage>
        ) : error ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => setReloadKey((key) => key + 1)} />
        ) : pageItems.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>
            {query.trim() ? `No organizations match “${query.trim()}”` : EMPTY_LABELS[accessFilter]}
          </DataTableMessage>
        ) : (
          <tbody>
            {pageItems.map((item) => {
              const to = orgBasePath(item.organizationId);
              const secondary = orgSecondaryLabel(item);
              return (
                <DataTableRow key={item.organizationId} to={to}>
                  <DataTableCell>
                    <Link to={to} className="font-medium text-foreground hover:underline">
                      {orgLabel(item)}
                    </Link>
                    <p className="text-xs text-muted-foreground">{secondary || item.organizationId}</p>
                  </DataTableCell>
                  <DataTableCell>
                    <StatusPill tone={item.studioAccess ? 'success' : 'neutral'}>
                      {item.studioAccess ? 'Access' : 'None'}
                    </StatusPill>
                  </DataTableCell>
                  <DataTableCell className="text-right tabular-nums">{formatCount(item.projectCount)}</DataTableCell>
                  <OrgSpendCell usage={item.usage30d} />
                  <DataTableCell>
                    <BillingCell productName={item.billing?.productName} status={item.billing?.status} />
                  </DataTableCell>
                  <DataTableCell className="max-w-[12rem]">
                    <span className="block truncate font-mono text-xs text-muted-foreground">
                      {item.fabric?.namespace || 'Not placed'}
                    </span>
                    {item.fabric ? <p className="truncate text-xs text-muted-foreground">{item.fabric.fabricId}</p> : null}
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </tbody>
        )}
      </DataTable>
    </div>
  );
}
